import { useRecoilCallback, useRecoilState, useSetRecoilState } from 'recoil';
import dayjs from 'dayjs';
import * as firebase from '@/lib/firebase/firestore';
import { tempMarkerState, markerPositionListState } from '@/state/markerState';
import { currentUserInfoState } from '@/state/userState';
import { currentCaptureInfoState } from '@/state/captureState';
import { pathState } from '@/state/appState';
import {
  currentCommentState,
  DEFAULT_CURRENT_COMMENT,
} from '@/state/commentState';
import { useQuerystring } from '@/hooks/useQuerystring';
import { showCommentsTabState } from '@/state/uiState';

import { responsiveDesignState } from '@/state/responsiveState';

// @ts-Ignore
export const useMarker = () => {
  const [markerPositionList, setMarkerPositionList] = useRecoilState(
    markerPositionListState
  );

  const setResponsiveDesign = useSetRecoilState(responsiveDesignState);

  const setShowCommentsTab = useSetRecoilState(showCommentsTabState);

  const { setQs } = useQuerystring();
  const resetTempMarker = useRecoilCallback(({ reset }) => () => {
    setShowCommentsTab(true);

    setResponsiveDesign({
      comments: true,
      sidebar: false,
      welcome: true,
    });

    reset(tempMarkerState);
  });

  const setTempMarker = useRecoilCallback(
    ({ set, snapshot }) =>
      async (args: Capture.Marker) => {
        const currentCaptureInfo = await snapshot.getLoadable(
          currentCaptureInfoState
        ).contents;
        const currentUserInfo = await snapshot.getLoadable(currentUserInfoState)
          .contents;

        const now = dayjs().unix();

        const data: Capture.Comment = {
          marker: args,
          updatedAt: now,
          content: '',
          createdAt: now,
          replies: [],
          replyEmojies: [],
          creator: {
            uid: currentUserInfo.uid,
            displayName: currentUserInfo.displayName || '',
            email: currentUserInfo.email || '',
            isAnonymous: currentUserInfo.isAnonymous,
          },
          isTemp: true,
        };
        // Here I've to put the things
        setShowCommentsTab(true);

        setResponsiveDesign({
          comments: true,
          sidebar: false,
          welcome: true,
        });
        set(tempMarkerState, data);
        const commentIndex = currentCaptureInfo.comments?.length || 0;

        set(currentCommentState, {
          ...DEFAULT_CURRENT_COMMENT,
          commentIndex: commentIndex,
          timestamp: now,
        });
        setQs('comment', commentIndex + 1);
      }
  );

  const updateMarkerPosition = useRecoilCallback(
    ({ set, snapshot }) =>
      async ({ isTemp, dx, dy, index }) => {
        const now = dayjs().unix();
        if (isTemp) {
          const tempMarker = (await snapshot.getLoadable(tempMarkerState)
            .contents) as Capture.Comment;
          // console.log('tempMarker', tempMarker);
          const x = tempMarker.marker.x + dx;
          const y = tempMarker.marker.y + dy;
          if(x === Infinity || x === -Infinity) return;
          if(y === Infinity || y === -Infinity) return;
          
          const newTempMarker = {
            ...tempMarker,
            updatedAt: now,
            marker: { ...tempMarker.marker, x, y },
          };
          set(tempMarkerState, newTempMarker);
        } else {
          const path = await snapshot.getLoadable(pathState).contents;
          const currentCaptureInfo = await snapshot.getLoadable(
            currentCaptureInfoState
          ).contents;
          // const currentUserInfo = await snapshot.getLoadable(currentUserInfoState)
          //   .contents;
          const { comments } = currentCaptureInfo;
          const newComments = [...comments];
          const x = newComments[index].marker.x + dx;
          const y = newComments[index].marker.y + dy;
          if(x === Infinity || x === -Infinity) return;
          if(y === Infinity || y === -Infinity) return;
          newComments[index] = {
            ...comments[index],
            updatedAt: now,
            marker: { ...newComments[index].marker, x, y },
          };
          set(currentCaptureInfoState, {
            ...currentCaptureInfo,
            comments: newComments,
          });
          firebase.setCaptureCommentReply(path.uid, path.cid, newComments);
        }
      }
  );

  const setMarkerPosition = (
    index: number,
    position: { x: number; y: number }
  ) => {
    setMarkerPositionList((pos) => [
      ...pos.slice(0, index),
      position,
      ...pos.slice(index),
    ]);
  };

  const getMarkerPosition = (index: number) => {
    return markerPositionList[index];
  };

  return {
    resetTempMarker,
    setTempMarker,
    updateMarkerPosition,
    setMarkerPosition,
    getMarkerPosition,
  };
};
